import {
    TableHeader, TableRow, Table, TableHead, TableBody, TableCell
} from 'shared/components/ui/table';
import {
    StatusDisplay
} from './BadgeStatus';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Tooltip, TooltipProvider, TooltipTrigger
} from 'shared/components/ui/tooltip';
import KBTooltipContent from './KBTooltipContent';
import {
    useScrollEndDetect
} from '../hooks/useScrollEndDetect';
import {
    useContext, useRef, useState
} from 'react';
import {
    KBContext
} from '../knowledgeBuilderContext/KBGlobalContext';
import {
    META_MAP_DIR
} from '../hooks/useFileHandler';
import {
    Input
} from 'shared/components/ui/input';
import {
    DocumentSectionContext
} from '../knowledgeBuilderContext/DocumentSectionContext';
import ChasingLine from './LoadingLine/LoadingLine';
import {
    ViewportList
} from 'react-viewport-list';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from 'shared/components/ui/dialog';
import DocViewer from './DocViewer';
import {
    Button
} from 'shared/components/ui/button';
import {
    Download, MoreVertical, Copy, Link2, AlertTriangle, Loader2
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from 'shared/components/ui/dropdown-menu';
import {
    useParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    kbWSService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/KnowledgeBuilder.websocket.service';
import {
    getCOSFormatDirectoryFromKey
} from '../hooks/utils/fileUtils';

/**
 * FileActionsMenu component with copy options
 * @component
 * @param {Object} props
 * @param {string} props.fileKey The file path/key
 * @param {string} props.presignedUrl The pre-signed URL
 */
function FileActionsMenu({
    fileKey, presignedUrl
}) {
    const [copyStatus, setCopyStatus] = useState({});

    const copyToClipboard = async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopyStatus({
                [type]: true
            });
            setTimeout(() => {
                setCopyStatus({});
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                    onClick={(e) => {
                        e.preventDefault();
                        copyToClipboard(fileKey, 'path');
                    }}
                    className="flex items-center gap-2"
                >
                    <Copy className="h-4 w-4" />
                    <span>{copyStatus.path ? 'Copied!' : 'Copy Path'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.preventDefault();
                        copyToClipboard(presignedUrl, 'url');
                    }}
                    className="flex items-center gap-2"
                    disabled={!presignedUrl}
                >
                    <Link2 className="h-4 w-4" />
                    <span>{copyStatus.url ? 'Copied!' : 'Copy Pre-signed URL'}</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function DocumentCell({
    children, tooltipText, className, py
}) {
    return (
        <TableCell className={`p-2 ${py || ''} truncate ${className || ''}`}>
            {tooltipText ? (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="truncate">{children}</div>
                        </TooltipTrigger>
                        <KBTooltipContent tooltipText={tooltipText} />
                    </Tooltip>
                </TooltipProvider>
            ) : (
                <div className="truncate">{children}</div>
            )}
        </TableCell>
    );
}

/**
 * @typedef DocumentStatus
 * @property {"success" | "failed" | "new" | string} status
 * @property {string | null | undefined} message
 */
/**
 *
 * @param {Object} props
 * @param {string} props.fileName File name of the file to be displayed in the table.
 * @param {string} props.uploader The one who upload the file in KB.
 * @param {string} props.origin The file where it is uploaded from.
 * @param {DocumentStatus} props.l1Status L1 Status of the file.
 * @param {DocumentStatus} props.l2Status L2 Status of the file.
 * @returns {React.JSX}
 */
function DocumentDataRow({
    mapDir,
    isSelected,
    onToggleSelect,
    fileName = '--',
    uploader = '--',
    // permission="--",
    origin = '--',
    l1Status = {
        status: '--'
    },
    l2Status = {
        status: '--'
    },
    metadata = {}
}
) {
    return (
        <>
            <TableRow className="hover:bg-muted/30 transition-colors grid grid-cols-[auto_2fr_1fr_1fr_1.2fr_1.2fr_auto]">
                <DocumentCell py="py-3">
                    <div className="items-center w-fit flex">
                        <Input
                            className="h-3 flex-shrink-0 shadow-none"
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleSelect(mapDir)}
                        />
                    </div>
                </DocumentCell>
                <DocumentCell tooltipText={fileName} py="py-3">
                    {metadata?.ibmOutput?.presigned_url ? (
                        <Dialog>
                            <DialogTrigger asChild>
                                <span>{fileName}</span>
                            </DialogTrigger>
                            <DialogContent className="min-w-[80vw] flex flex-col min-h-[80vh]">
                                <DialogHeader>
                                    <div className="flex items-center gap-3">
                                        <DialogTitle>View Document</DialogTitle>
                                        <Button
                                            variant="ghost"
                                            className="flex h-fit w-fit items-center gap-2"
                                            onClick={() => {
                                                if (metadata?.ibmOutput?.presigned_url) {
                                                    const link = document.createElement('a');
                                                    link.href = metadata.ibmOutput.presigned_url;
                                                    link.download = fileName;
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                }
                                            }}>
                                            <Download size={18} />
                                            Download
                                        </Button>
                                    </div>
                                </DialogHeader>

                                <div className="flex-grow flex flex-col">
                                    <div className="flex-grow h-0">
                                        <DocViewer document={{
                                            ...metadata.ibmOutput,
                                            document_name: fileName
                                        }} name={fileName} page={1} />
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    ) : (
                        <span>{fileName}</span>
                    )}
                </DocumentCell>
                <DocumentCell tooltipText={uploader} py="py-3">{uploader}</DocumentCell>
                {/* <DocumentCell tooltipText={permission}>{permission}</DocumentCell> */}
                <DocumentCell tooltipText={origin} py="py-3">{origin}</DocumentCell>
                <DocumentCell py="py-3"><StatusDisplay status={l1Status.status} tooltipText={l1Status.message} /></DocumentCell>
                <DocumentCell py="py-3"><StatusDisplay status={l2Status.status} tooltipText={l2Status.message} /></DocumentCell>
                <DocumentCell py="py-3">
                    <FileActionsMenu
                        fileKey={metadata?.ibmOutput?.Key || mapDir}
                        presignedUrl={metadata?.ibmOutput?.presigned_url}
                    />
                </DocumentCell>
            </TableRow>
        </>
    );
}

/**
 * This compoenent contains the Folder list and as well as the document table which contains some of the interface to add, delete, create folder components.
 * @component
 * @returns {React.JSX}
 */
export default function DocumentTable() {
    const {
        selectedFolder, handleCheckMetadata, getMemberNameByUserId, loadDocuments, continuationToken, updateFileStatus, l1Status
    } = useContext(KBContext);
    const {
        isSelected,
        isAllSelected,
        toggleDocSelection,
        handleSelectAll,
        getVisibleDocs,
        documentIsLoading
    } = useContext(DocumentSectionContext);
    const {
        oragID, subOragID, collectionId
    } = useParams();
    const {
        user
    } = useContext(Context);
    const [startingL1, setStartingL1] = useState(false);

    const reachedScrollEnd = () => {

        if (continuationToken.current && !documentIsLoading) loadDocuments();
    };

    const scrollAreaRef = useRef(null);
    // useEffect(()=>{
    //
    //
    // },[documentIsLoading, getVisibleDocs])
    const handleScroll = useScrollEndDetect(50, 100, reachedScrollEnd);

    // While an L1 run is active on the collection, show not-yet-indexed rows as
    // "Processing" immediately — Start with Filter "All" doesn't pre-mark rows,
    // so without this the button spins but the table looks idle.
    const collectionL1Busy = l1Status === 'processing';

    const docs = selectedFolder ? getVisibleDocs().map((doc) => {
        const mapDir = selectedFolder.get(doc)?.get(META_MAP_DIR);
        if (mapDir && handleCheckMetadata(mapDir, 'type') === 'file') {
            const l1Meta = {
                ...handleCheckMetadata(mapDir, 'status')
            };
            const l1StatusValue = String(l1Meta.status || '').toLowerCase();
            if (collectionL1Busy && l1StatusValue !== 'success') {
                l1Meta.status = 'processing';
            }
            return {
                key: mapDir,
                fileName: handleCheckMetadata(mapDir, 'name'),
                mapDir,
                isSelected: isSelected(mapDir),
                uploader: getMemberNameByUserId(handleCheckMetadata(mapDir, 'uploader')),
                origin: handleCheckMetadata(mapDir, 'origin') || '--',
                l1Status: l1Meta,
                l2Status: {
                    ...handleCheckMetadata(selectedFolder.get(doc).get(META_MAP_DIR), 'l2_status'),
                },
                ibmOutput: {
                    ...handleCheckMetadata(selectedFolder.get(doc).get(META_MAP_DIR), 'otherData')
                }
            };
        }
        return null;
    }).filter(Boolean) : [];

    // Files that have been uploaded/changed but are NOT yet L1-indexed (so they
    // are invisible to RAG). "success" = searchable; in-flight states are
    // already being processed; everything else (--, idle, new, failed) needs L1.
    const DONE = 'success';
    const INFLIGHT = ['processing', 'uploading', 'checking'];
    const needsL1 = docs.filter((d) => {
        const s = String(d.l1Status?.status || '').toLowerCase();
        return s !== DONE && !INFLIGHT.includes(s);
    });

    const startL1ForUnprocessed = async () => {
        if (!needsL1.length || startingL1) return;
        const cosKeys = needsL1.map((d) => getCOSFormatDirectoryFromKey(d.key));
        setStartingL1(true);
        try {
            await kbWSService.get_l1_task_id_with_creds({
                organization_id: oragID,
                sub_organization_id: subOragID,
                collection_id: collectionId,
                prefix: '',
                status_filter: '',
                // Global Augmentation (GA) — the balanced speed/accuracy mode the
                // sidebar recommends. Keeps banner-processed files at the same
                // retrieval quality users get when they pick GA manually.
                contextual_embedding_strategy: 'GLOBAL',
                file_keys_filter: cosKeys,
                user: user?.name || 'Unrecognized user'
            });
            updateFileStatus(cosKeys, 'processing');
        } catch (err) {
            // Non-destructive: leave file statuses as-is so the banner stays.
            console.error('Failed to start L1 for unprocessed files', err);
        } finally {
            setStartingL1(false);
        }
    };

    return (
        <>
            <div className="flex-grow h-0 flex flex-col pb-2 border-t border-border bg-background">
                {needsL1.length > 0 && (
                    <div className="flex items-center justify-between gap-3 border-b border-amber-300/60 bg-amber-50 px-4 py-2 text-[13px] text-amber-900">
                        <div className="flex items-center gap-2 min-w-0">
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">
                                <b>{needsL1.length}</b>{' '}
                                {needsL1.length > 1 ? 'files are' : 'file is'} uploaded but not yet
                                searchable. Run L1 so {needsL1.length > 1 ? 'they' : 'it'} can be used by assistants.
                            </span>
                        </div>
                        <Button
                            size="sm"
                            className="flex-shrink-0"
                            onClick={startL1ForUnprocessed}
                            disabled={startingL1}
                        >
                            {startingL1 && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                            {startingL1 ? 'Starting…' : 'Process now (L1)'}
                        </Button>
                    </div>
                )}
                <Table>
                    <TableHeader className="bg-muted/40 sticky top-0 z-10">
                        <TableRow className="border-b border-border hover:bg-transparent grid grid-cols-[auto_2fr_1fr_1fr_1.2fr_1.2fr_auto]">
                            <TableHead className="h-10 p-2 flex items-center justify-center w-10">
                                <Input
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    type="checkbox"
                                    checked={isAllSelected()}
                                    onChange={handleSelectAll}
                                />
                            </TableHead>
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Filename</TableHead>
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">Uploaded By</TableHead>
                            {/* <TableHead className="text-neutral-500 p-2 items-center flex">Permission</TableHead> */}
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Origin</TableHead>
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">L1 Status</TableHead>
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">L2 Status</TableHead>
                            <TableHead className="h-10 p-2 flex items-center text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                </Table>

                <ScrollArea onScroll={handleScroll} className="relative flex-grow" ref={scrollAreaRef}>
                    {documentIsLoading ? <ChasingLine /> : null}
                    <Table>
                        <TableBody>
                            {selectedFolder && docs.length > 0 ? (
                                <ViewportList viewportRef={scrollAreaRef} items={docs}>
                                    {(doc) => (
                                        <DocumentDataRow
                                            key={doc.key}
                                            fileName={doc.fileName}
                                            mapDir={doc.mapDir}
                                            isSelected={doc.isSelected}
                                            onToggleSelect={toggleDocSelection}
                                            uploader={doc.uploader}
                                            origin={doc.origin}
                                            l1Status={doc.l1Status}
                                            l2Status={doc.l2Status}
                                            metadata={{
                                                ibmOutput: doc.ibmOutput
                                            }}

                                        />
                                    )}
                                </ViewportList>
                            ) : !documentIsLoading ? (
                                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                                    <div className="bg-muted/50 p-4 rounded-full mb-3">
                                        <Download className="h-8 w-8 opacity-50" />
                                    </div>
                                    <p className="font-medium">No documents found</p>
                                    <p className="text-sm text-muted-foreground/80">Upload documents to get started</p>
                                </div>
                            ) : null}
                        </TableBody>
                    </Table>
                </ScrollArea>

            </div>
        </>
    );
}